import { usePetPracticeReward } from '../../teachersPet/usePetPracticeReward.js';
// src/components/practice/PracticeQuiz.jsx
//
// GENERIC, reusable practice quiz (set-text / literature / skills mount the same
// one). Pools the pack's `quiz` items into one auto-graded MCQ activity, faithful
// to the prototype LitQuiz visual: every question on one page, per-question Submit
// -> reveal correct/wrong + explanation, a live + final score, and Retry.
//
// Reuses the shared quiz engine (toQuizQuestions) for mapping + validation and
// countCorrect for scoring -- NO duplicated grading logic. Session state (answers +
// submitted, keyed by stable q.id) can be manually SAVED + RESUMED per pack via a
// Firestore quizState blob (signed-in only); the signed-out/mock path stays
// in-session-only. NO AI / server / "Submit for review" (that is L3).
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { toQuizQuestions } from '../../pages/setTextReaderGate.js';
import { ReadingPrefsContext } from '../../pages/useReadingPrefs.js';
import { portalSkinClass } from '../../pages/readingPrefs.js';
import { countCorrect, isAnswerComplete } from './practiceHelpers.js';
import { decodeQuizState, pruneToKeys, progressPercent } from '../../storage/progressHelpers.js';
import { remainingLabel, drillDoneMessage } from './feedbackState.js';
import QuizQuestion from './QuizQuestion.jsx';
import '../reader/lesson.css';

// Fixed section order for the grouped view. `type` matches q.type out of toQuizQuestions
// ('mcq' | 'fill' | 'matching'); `label` is the on-brand section header.
const GROUP_ORDER = [
    { type: 'mcq', label: 'Multiple Choice' },
    { type: 'fill', label: 'Fill in the Blanks' },
    { type: 'matching', label: 'Matching' },
];

// `embedded` (default false): the quiz is mounted INLINE inside other content (the
// per-section "Drill it" block) rather than as the full-page Practice view. Embedded
// mounts suppress the two full-page-only behaviours that misfire mid-page: the
// resume auto-scroll-to-window-top (the drill's resume banner is already where the
// user is heading; scrolling to top would move AWAY from it) and the floating
// Save/Top portals (reading a long section must not summon quiz controls -- the
// inline Save Progress button in the progress row still covers saving).
export default function PracticeQuiz({ items, showLockedCta, onComplete, persistEnabled, savedQuizState, onSaveProgress, embedded = false }) {
    const { colorMode } = useContext(ReadingPrefsContext);
    const { reward: petReward, checkReward } = usePetPracticeReward();
    const questions = useMemo(() => toQuizQuestions(items), [items]);
    const [answers, setAnswers] = useState({});     // qKey (stable q.id) -> answer value
    const [submitted, setSubmitted] = useState({}); // qKey (stable q.id) -> true (locked + revealed)
    const [showTop, setShowTop] = useState(false);  // back-to-top button visibility
    const [syncStatus, setSyncStatus] = useState(''), [syncError, setSyncError] = useState(false);
    const lastReport = useRef(null);
    const [justSaved, setJustSaved] = useState(false);       // transient "Saved" confirmation
    const [resumeHandled, setResumeHandled] = useState(false); // resume prompt dismissed (resume or start over)
    const didAutoScrollRef = useRef(false);          // auto-scroll-to-resume-banner fired once per question set

    // Reset if the question set changes (e.g. paid quizzes arrive after entitlement). Also
    // re-arm the resume prompt + auto-scroll so a freshly-loaded question set can offer +
    // surface its saved blob.
    useEffect(() => { setAnswers({}); setSubmitted({}); setSyncStatus(''); setSyncError(false); lastReport.current = null; setResumeHandled(false); didAutoScrollRef.current = false; }, [questions.map(q => q.id).join('|')]);

    // Clear the transient "Saved" confirmation after a moment.
    useEffect(() => {
        if (!justSaved) return undefined;
        const t = setTimeout(() => setJustSaved(false), 2000);
        return () => clearTimeout(t);
    }, [justSaved]);

    // Show the floating "Back to top" button once the reader is scrolled down a bit. The
    // reader scrolls with the WINDOW (ReaderPageShell is min-h, not an overflow container),
    // so we watch window.scrollY. Threshold kept small (250px) so it appears after a short
    // scroll, not only deep in a long quiz.
    useEffect(() => {
        if (embedded) return undefined; // embedded: no floating controls -> no listener
        const onScroll = () => setShowTop(window.scrollY > 250);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, [embedded]);

    // Group questions by type into fixed-order sections (Multiple Choice, Fill in the
    // Blanks, Matching), preserving each question's original relative order within its
    // type. Each entry keeps its ORIGINAL index `i` so all answer/submit state (keyed by
    // i) stays attached after reordering -- only the DISPLAY order changes. `number` is a
    // continuous running counter across non-empty groups (Q1..Qn overall). Empty groups
    // are dropped so no section header renders without questions.
    const groups = useMemo(() => {
        const byType = { mcq: [], fill: [], matching: [] };
        questions.forEach((q, i) => {
            const t = byType[q.type] ? q.type : 'mcq'; // default/unknown -> mcq bucket
            byType[t].push({ q, i });
        });
        let running = 0;
        return GROUP_ORDER
            .map(({ type, label }) => ({ label, items: byType[type] }))
            .filter((g) => g.items.length > 0)
            .map((g) => ({ label: g.label, items: g.items.map((e) => ({ ...e, number: ++running })) }));
    }, [questions]);
    // Headers only add value when more than one type is present; a single-type pack (e.g.
    // MCQ-only) renders exactly as before, with no lone section header.
    const showHeaders = groups.length > 1;

    // Quick-jump: one ref per group section header (keyed by the group's unique label).
    // Headers only render when showHeaders is true, i.e. exactly when the pill row shows,
    // so every pill always resolves to a live header ref.
    const sectionRefs = useRef({});

    // Stable per-question key: the item id (q.id, stable across reordering / count
    // changes) so answer + submit state binds to the QUESTION, not its array position.
    // Falls back to a positional key ONLY if a question somehow lacks an id (defensive;
    // real items always carry one). Display numbering stays position-based (the grouped
    // view's `number`) -- only the state key is id-based.
    const keyFor = (q, i) => (q && q.id) || `idx-${i}`;

    // countCorrect iterates questions BY INDEX (reads a[i]); build an index-keyed view of
    // the id-keyed answers so the shared scorer stays byte-for-byte unchanged.
    // How many CURRENT questions are submitted (counts only keys that map to a live
    // question -- robust if a stale key ever lingers in the map).
    const submittedCount = questions.reduce((n, q, i) => (submitted[keyFor(q, i)] ? n + 1 : n), 0);

    // Resume: decode the saved blob and prune to CURRENT question keys (drop answers /
    // submits for questions since removed or reordered out). The prompt only offers a
    // resume when persistence is on, the blob has content, the user has NOT dismissed it,
    // and they have no in-session work yet (so we never clobber a live attempt).
    const currentKeys = new Set(questions.map((q, i) => keyFor(q, i)));
    const savedDecoded = decodeQuizState(savedQuizState);
    const savedAnswers = pruneToKeys(savedDecoded.answers, currentKeys);
    const savedSubmitted = pruneToKeys(savedDecoded.submitted, currentKeys);
    const savedSubmittedCount = Object.keys(savedSubmitted).length;
    const savedHasContent = savedSubmittedCount > 0 || Object.keys(savedAnswers).length > 0;
    const hasInSessionWork = Object.keys(answers).length > 0 || submittedCount > 0;
    const showResumePrompt = !!persistEnabled && !resumeHandled && savedHasContent && !hasInSessionWork;

    const reportAnswers = (nextSubmitted) => {
        const answered = questions.filter((q,i) => nextSubmitted[keyFor(q,i)]).length;
        const score = countCorrect(questions, questions.map((q,i) => nextSubmitted[keyFor(q,i)] ? answers[keyFor(q,i)] : undefined));
        lastReport.current = { score, total: questions.length, answered };
        syncReport();
    };
    const syncReport = async () => {
        if (!onComplete || !lastReport.current) return;
        setSyncStatus('Saving answers…'); setSyncError(false);
        try { await onComplete(lastReport.current); setSyncStatus(persistEnabled ? 'Results saved to your learning report.' : 'Sign in to save your progress.'); }
        catch { setSyncStatus('Could not save answers. Check your connection, then retry.'); setSyncError(true); }
    };

    // Auto-scroll to the top ONCE when a resumable session first loads, so the resume
    // banner (rendered at the top) is actually seen -- students might not scroll up on
    // their own. Guarded by a ref so it fires once per question set and never fights the
    // user's own scrolling afterwards. A fresh quiz (showResumePrompt false) is not scrolled.
    // FULL-PAGE MOUNTS ONLY: in an embedded drill the banner sits mid-page at the top of
    // the drill block, so a window-top scroll would yank the reader AWAY from it (and
    // re-fire on every section revisit via the per-section key remount) -- skipped.
    useEffect(() => {
        if (embedded) return;
        if (showResumePrompt && !didAutoScrollRef.current) {
            didAutoScrollRef.current = true;
            if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [showResumePrompt, embedded]);

    if (questions.length === 0) {
        return (
            <div className="max-w-3xl mx-auto text-center py-16">
                <p className="font-serif italic text-book-muted text-lg">No quiz questions available yet.</p>
            </div>
        );
    }

    const allDone = submittedCount === questions.length;
    // Live score: count correct among ONLY the submitted questions (index-keyed view
    // where a question's answer is present only when that question is submitted).
    const score = countCorrect(
        questions,
        questions.map((q, i) => (submitted[keyFor(q, i)] ? answers[keyFor(q, i)] : undefined)),
    );
    const setAnswer = (key, v) => setAnswers((a) => ({ ...a, [key]: v }));
    const submitOne = (key) => { const next = { ...submitted, [key]: true }; setSubmitted(next); reportAnswers(next);
      const raw = questions.filter(q => q.id && next[q.id]).map(q => ({ id: q.id, answer: answers[q.id] }));
      checkReward(raw);
    };
    // Per-question retry: unlock ONE submitted question so it can be redone. Clears only
    // submitted[key] (KEEPS answers[key] so the widget stays pre-filled + editable) ->
    // QuizQuestion re-enables inputs + hides the reveal, and the Submit button reappears.
    // Update the reported submitted count as well; the resumable answer blob remains
    // separately controlled by Save Progress.
    const retryOne = (key) => { const next = { ...submitted }; delete next[key]; setSubmitted(next); reportAnswers(next); };
    const reset = () => { setAnswers({}); setSubmitted({}); };
    // Quiz completion for the progress bar (submitted / total).
    const quizPct = progressPercent(submittedCount, questions.length);
    // Manual save: hand the id-keyed session up to the pack reader's saveQuizState.
    const doSave = () => { if (onSaveProgress) { onSaveProgress({ answers, submitted }); setJustSaved(true); } };
    // Resume hydrates the pruned saved maps; Start over leaves the blob in Firestore
    // (last-write-wins on the next Save) and just begins fresh.
    const doResume = () => { setAnswers(savedAnswers); setSubmitted(savedSubmitted); setResumeHandled(true); };
    const doStartOver = () => setResumeHandled(true);
    // Smooth-scroll a group's header into view. `scroll-mt-32` on the header (below) leaves
    // room for the fixed navbar (h-16) + the sticky pill row so the header lands clear of both.
    const scrollToGroup = (label) => {
        const el = sectionRefs.current[label];
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="max-w-3xl">
            {petReward && <p role="status" className="text-xs text-book-muted mb-3">{petReward}</p>}
            {syncStatus && <p role="status" className="text-xs text-book-muted mb-3">{syncStatus}{syncError && <button className="underline ml-2" onClick={syncReport}>Retry saving</button>}</p>}
            {/* Resume prompt: only when persistence is on, a non-empty blob exists, and the
                user has no in-session work yet. Theme-aware (book-* tokens) in all modes. */}
            {showResumePrompt && (
                <div className="mb-6 border border-book-accent/40 bg-book-accent/5 p-4">
                    <div className="font-display text-book-accent text-xs mb-1 font-semibold">Resume practice</div>
                    <p className="font-serif italic text-book-muted text-sm mb-3">
                        You saved progress here: {savedSubmittedCount} of {questions.length} answered ({progressPercent(savedSubmittedCount, questions.length)}% complete). Resume where you left off?
                    </p>
                    <div className="flex gap-2">
                        <button onClick={doResume}
                            className="px-4 py-2 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                            Resume
                        </button>
                        <button onClick={doStartOver}
                            className="px-4 py-2 border border-book-accent/40 text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors font-semibold">
                            Start over
                        </button>
                    </div>
                </div>
            )}

            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div>
                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">Practice · Quiz</div>
                    <h2 className="font-display text-xl md:text-2xl text-book-accent leading-tight">Test your understanding</h2>
                </div>
                {/* 3b: a compact "N to go" nudge alongside the running score -- the
                    thing a student actually wants to know mid-drill. */}
                <div className="flex items-center gap-3">
                    {remainingLabel(submittedCount, questions.length) && (
                        <span className="px-2.5 py-1 rounded-full bg-book-accent/10 font-display text-book-accent text-xs font-semibold">
                            {remainingLabel(submittedCount, questions.length)}
                        </span>
                    )}
                    <span className="font-serif italic text-book-muted text-sm">{submittedCount} / {questions.length} answered · score {score}</span>
                </div>
            </div>

            {/* Quiz progress bar (submitted / total) -- mirrors the reader sidebar PROGRESS
                bar. Shown to everyone; the Save button appears only when persistence is on
                (signed-in, non-mock) so signed-out users see no non-functional control. */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-1">
                    <span className="font-display text-book-muted text-xs font-semibold">Progress</span>
                    <div className="flex items-center gap-3">
                        <span className="font-display text-book-accent text-xs font-semibold">{quizPct}%</span>
                        {persistEnabled && (
                            <button onClick={doSave}
                                className="px-3 py-1.5 border border-book-accent/40 text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors font-medium rounded-full">
                                {justSaved ? 'Saved' : 'Save Progress'}
                            </button>
                        )}
                    </div>
                </div>
                <div className="h-1.5 bg-book-accent/10 rounded-full overflow-hidden">
                    <div className="h-full bg-book-accent transition-all duration-500" style={{ width: quizPct + '%' }} />
                </div>
            </div>

            {/* Sticky quick-jump pills: one per non-empty type group, in the fixed group
                order, with its question count. Sticks below the fixed navbar (top-16 = h-16)
                so it stays reachable while scrolling; only shown when there's more than one
                group to jump between (MCQ-only pack renders as before, no pills). */}
            {groups.length > 1 && (
                <div className="sticky top-16 z-20 flex flex-wrap gap-2 mb-6 py-2 bg-book-bg">
                    {groups.map((g) => (
                        <button key={g.label} onClick={() => scrollToGroup(g.label)}
                            className="px-3 py-1.5 border border-book-accent/40 text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors font-medium rounded-full">
                            {g.label} ({g.items.length})
                        </button>
                    ))}
                </div>
            )}

            <div className="space-y-8">
                {groups.map((g) => (
                    <div key={g.label} className="space-y-5">
                        {showHeaders && (
                            <div ref={(el) => { sectionRefs.current[g.label] = el; }}
                                className="font-display text-book-accent text-xs border-b border-book-accent/20 pb-2 scroll-mt-32 font-semibold">
                                {g.label}
                            </div>
                        )}
                        {g.items.map(({ q, i, number }) => {
                            const qKey = keyFor(q, i);
                            const isSubmitted = !!submitted[qKey];
                            return (
                                <div key={q.id || i} className="btl-quiz-card border border-book-accent/20 bg-book-page/30 p-5 text-book-accent">
                                    <div className="font-display text-book-accent text-xs mb-3 font-semibold">Question {number}</div>
                                    <h3 className="font-display text-book-text text-base leading-relaxed mb-4">{q.question}</h3>
                                    <QuizQuestion q={q} answer={answers[qKey]} setAnswer={(v) => setAnswer(qKey, v)} submitted={isSubmitted} />
                                    {!isSubmitted ? (
                                        <div className="flex justify-end mt-4">
                                            <button onClick={() => submitOne(qKey)} disabled={!isAnswerComplete(q, answers[qKey])}
                                                className="px-5 py-2.5 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors disabled:opacity-30 disabled:cursor-not-allowed font-semibold">
                                                Submit Answer
                                            </button>
                                        </div>
                                    ) : (
                                        /* Subtle per-question retry: unlocks THIS question (keeps the answer,
                                           hides the reveal, re-shows Submit). Distinct from the bottom
                                           "Retry Quiz" which resets the whole run. */
                                        <div className="flex justify-end mt-4">
                                            <button onClick={() => retryOne(qKey)}
                                                className="px-4 py-2 border border-book-accent/40 text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors font-semibold">
                                                Try Again
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* 3b completion card. One small 300ms scale/fade (btl-drill-done), no
                confetti library, and reduced-motion turns the animation off entirely.
                Nothing new is persisted -- onComplete already reported the run. */}
            {allDone && (
                <div className="btl-drill-done mt-6 rounded-[var(--radius-card)] border border-book-success/50 bg-book-success/10 shadow-[var(--shadow-card)] p-5 text-center">
                    <div className="font-display text-book-text text-2xl font-bold mb-1">
                        {drillDoneMessage(score, questions.length)} &middot; {score}/{questions.length}
                    </div>
                    <p className="font-serif italic text-book-muted text-sm mb-4">
                        {score === questions.length ? 'Full marks.' : 'The explanations above show what each one was after.'}
                    </p>
                    <button onClick={reset}
                        className="px-5 py-2 rounded-full bg-book-accent text-book-pageDark font-display text-sm font-semibold">
                        Retry
                    </button>
                </div>
            )}

            {showLockedCta && (
                <div className="mt-6 max-w-md mx-auto border border-book-accent/20 bg-book-bg/40 p-3 text-center">
                    <div className="font-display text-book-muted/80 text-xs mb-1 font-semibold">🔒 More with purchase</div>
                    <p className="font-serif italic text-book-muted/70 text-xs leading-snug font-semibold">More practice unlocks with the pack.</p>
                </div>
            )}

            {/* Floating controls (bottom-right, portaled to body). Save stacks ABOVE
                back-to-top so both stay reachable while scrolled; Save appears only when
                persistence is on (signed-in, non-mock) and shares doSave + justSaved with
                the top button. Both gated on showTop so Save is never orphaned above an
                absent Top button. */}
            {!embedded && showTop && persistEnabled && <FloatingSaveButton colorMode={colorMode} onSave={doSave} justSaved={justSaved} />}
            {!embedded && showTop && <BackToTopButton colorMode={colorMode} />}
        </div>
    );
}

// Floating back-to-top, PORTALED to document.body. It must live outside the reader shell
// (ReaderPageShell's .page-fade-in keeps a persistent `transform: translateY(0)` from its
// `forwards` fade animation, which would make position:fixed anchor to that full-height div
// -- the QT4 bug where the button only showed at the very bottom of the content). Portaling
// to body escapes that containing block so `fixed` is truly viewport-relative. Portals keep
// React context but NOT CSS ancestor classes, so the wrapper re-applies the theme skin
// (portalSkinClass) -- same pattern as the AI/Notes drawers -- keeping book-* tokens correct
// in all three modes. bottom-right avoids the left sidebar + the top sticky pills; z-40 sits
// below the z-50 navbar.
function BackToTopButton({ colorMode }) {
    if (typeof document === 'undefined') return null;
    return createPortal(
        <div className={portalSkinClass(colorMode)}>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="Back to top"
                className="fixed bottom-6 right-6 z-40 px-3 py-1.5 border border-book-accent/40 bg-book-bg text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors shadow-lg font-medium rounded-full">
                &uarr; Top
            </button>
        </div>,
        document.body,
    );
}

// Floating "Save", PORTALED to document.body -- same escape-the-reader-shell pattern +
// portalSkinClass theming as BackToTopButton (see its note). Lets a student save from any
// scroll position. Compact pill scale (px-3 py-1.5 text-[10px], matching the quick-jump
// pills + the back-to-top button) with a short "Save" label so it reads as a small refined
// twin of "^ Top", not a bulky button. Sits at bottom-16 (just above the bottom-6 back-to-
// top) so the two stack without overlap, right-aligned; z-40 below the z-50 navbar. Reuses
// the parent's doSave handler + justSaved flash -- no duplicated save logic.
function FloatingSaveButton({ colorMode, onSave, justSaved }) {
    if (typeof document === 'undefined') return null;
    return createPortal(
        <div className={portalSkinClass(colorMode)}>
            <button onClick={onSave}
                aria-label="Save progress"
                className="fixed bottom-16 right-6 z-40 px-3 py-1.5 border border-book-accent/40 bg-book-bg text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors shadow-lg font-medium rounded-full">
                {justSaved ? 'Saved' : 'Save'}
            </button>
        </div>,
        document.body,
    );
}
